/**
 * ServiceProximityMap — a props-driven proximity map that reuses the same
 * markers, popover, and controls as ActiveServiceMap but without any
 * MapRoutingContext dependency. Intended for use in ScheduleStopDialog
 * and any other context that needs a proximity visualization.
 *
 * Requirements:
 * - Must be rendered inside a FormProvider with fields:
 *   startDate, endDate, radiusCommitted, selectedDays.*, measure, material, action, businessLine
 * - Must be rendered inside an APIProvider (Google Maps)
 */
import { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Map, ControlPosition, MapControl, AdvancedMarker } from '@vis.gl/react-google-maps'
import { Box, SvgIcon, Typography, useTheme } from '@mui/material'
import { common } from '@mui/material/colors'
import { ReactComponent as CSMarker } from 'assets/map/CSMarker.svg'
import AdaptiveRadiusCircle from 'components/configured-service/Schedule/AdaptiveRadiusCircle'
import AdaptiveServiceMarkerLayer from 'components/configured-service/Schedule/AdaptiveServiceMarkerLayer'
import MapRadiusControl from 'components/configured-service/Schedule/MapRadiusControl'

import MapTypeControlWrapper from 'components/common/map/MapTypeControlWrapper'
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, GOOGLE_MAPS_MAX_ZOOM } from 'components/common/map/constants'
import FitBoundsControl from 'components/common/map/FitBoundsControl'
import { useSharedMarkerPopover } from 'components/configured-service/Schedule/hooks/useSharedMarkerPopover'
import SchedulingMapServicePopover from 'components/configured-service/Schedule/SchedulingMapServicePopover'
import SkeletonSchedulingMapServicePopover from 'components/configured-service/Schedule/SkeletonSchedulingMapServicePopover'
import { useConfiguredServiceMapData } from 'components/configured-service/hooks/useConfiguredServiceMapData'
import { StyledInfoWindow } from 'components/configured-service/Schedule/MapStyles'
import { useMapVisibleMarkers } from 'components/configured-service/hooks/useMapVisibleMarkers'
import MarkerTypeControl from 'components/configured-service/tabs/MapRoutingTab/components/MarkerTypeControl'
import FitBounds from 'components/configured-service/tabs/MapRoutingTab/components/ActiveServiceEditMap/FitBounds'
import { FRONTEND_DATE_FORMAT_DATE_FNS } from 'settings/constants/date'
import { format as formatDate } from 'date-fns'
import { mapServiceDetails } from 'components/configured-service/utils/serviceDetailsMapper'
import { useProximityMapFormState } from 'components/configured-service/hooks/useProximityMapFormState'
import { useMapTypeControl } from 'components/configured-service/hooks/useMapTypeControl'
import { useLocationDerivedState } from 'components/configured-service/hooks/useLocationDerivedState'
import { getCanRenderIconLayer, useWebGLSupport } from 'components/common/map/webgl/useWebGLSupport'
import { SCHEDULING_MAP_SURFACE } from 'slices/configuredService/configuredServiceScheduleSlice'

const ServiceProximityMap = ({ latitude, longitude, mapMarkerType = 'weekDay', onMarkerTypeChange, filtersExpanded = false }) => {
  const theme = useTheme()
  const webglSupported = useWebGLSupport()
  // The circle and pin are drawn inside the marker layer's deck.gl canvas, and WebGLMarkerSurface
  // only takes that path when the IconLayer texture probe passes too. Gate the DOM equivalents on
  // the same condition, or a device that clears basic WebGL2 but fails the probe gets markers with
  // no radius circle and no center pin.
  const webglMarkersActive = webglSupported && getCanRenderIconLayer()

  const [fitBoundsHandler, setFitBoundsHandler] = useState(() => () => {})
  const { mapTypeId, handleMapTypeChange } = useMapTypeControl()
  const { radiusCommitted, selectedDaysMap, selectedRoutesMap, startDate, endDate } = useProximityMapFormState()
  const { centerLatLng, showCenterElements, zoomControlOptions } = useLocationDerivedState(latitude, longitude)

  const {
    data,
    isLoading: isMapDataLoading,
    isSuccess: isMapDataSuccess,
    truncated,
    totalMatched,
    cap,
  } = useConfiguredServiceMapData({
    latitude,
    longitude,
    surface: SCHEDULING_MAP_SURFACE.SCHEDULE_STOP,
  })

  const { setViewportBounds, deferredMarkers: viewportMarkers } = useMapVisibleMarkers(data, isMapDataLoading)

  // In route mode, hide markers whose routes are all deselected.
  // Unassigned services (no routes) are always shown regardless of route selection.
  const deferredMarkers = useMemo(() => {
    if (mapMarkerType !== 'route') return viewportMarkers
    const anyDeselected = Object.values(selectedRoutesMap).some(v => v === false)
    if (!anyDeselected) return viewportMarkers
    return viewportMarkers.filter(marker => {
      const routes = marker.routes || []
      if (!routes.length) return true
      return routes.some(r => selectedRoutesMap[r.routeId] !== false)
    })
  }, [viewportMarkers, mapMarkerType, selectedRoutesMap])

  const radiusCircle = useMemo(
    () =>
      showCenterElements
        ? {
            center: centerLatLng,
            radius: Math.max(0, Math.round(radiusCommitted || 0)),
            strokeColor: theme.palette.primary.dark,
            strokeOpacity: 1,
            strokeWeight: 3,
            fillColor: theme.palette.primary.main,
            fillOpacity: 0.3,
          }
        : null,
    [showCenterElements, centerLatLng, radiusCommitted, theme.palette.primary.dark, theme.palette.primary.main]
  )

  const centerMarker = useMemo(
    () => (showCenterElements ? { position: centerLatLng, bgColor: theme.palette.primary.main } : null),
    [showCenterElements, centerLatLng, theme.palette.primary.main]
  )

  const {
    activeMarker,
    infoWindowShown,
    serviceDetails,
    weekWorkOrders,
    isLoadingPopover,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
  } = useSharedMarkerPopover({ startDate, endDate })

  const handleCameraChange = useCallback(
    ev => {
      if (ev.detail.bounds) {
        setViewportBounds(ev.detail.bounds)
      }
    },
    [setViewportBounds]
  )

  const enrichedService = mapServiceDetails(serviceDetails, activeMarker?.details)

  return (
    <Box flex={1} width="100%" height="100%" sx={{ position: 'relative' }}>
      <Map
        disableDefaultUI
        zoomControl
        zoomControlOptions={zoomControlOptions}
        mapId="service-proximity-map"
        defaultCenter={showCenterElements ? centerLatLng : DEFAULT_MAP_CENTER}
        defaultZoom={showCenterElements ? 13 : DEFAULT_MAP_ZOOM}
        maxZoom={GOOGLE_MAPS_MAX_ZOOM}
        onCameraChanged={handleCameraChange}
        mapTypeId={mapTypeId}
        gestureHandling="greedy"
      >
        <FitBounds
          data={data}
          latitude={latitude}
          longitude={longitude}
          isSuccess={isMapDataSuccess}
          radius={radiusCommitted}
          mapMarkerType={mapMarkerType}
          onFitBounds={setFitBoundsHandler}
        />
        <FitBoundsControl fitBounds={fitBoundsHandler} position={ControlPosition.TOP_RIGHT} />

        {/* This dialog collapses its filters by default, so the on-map slider is often the only
            radius control the user can see. */}
        <MapControl position={ControlPosition.BOTTOM_LEFT}>
          <MapRadiusControl truncated={isMapDataSuccess && !isMapDataLoading && truncated} totalMatched={totalMatched} cap={cap} />
        </MapControl>
        <MapTypeControlWrapper value={mapTypeId} onChange={handleMapTypeChange} />

        {startDate && endDate && (
          <MapControl position={ControlPosition.TOP_CENTER}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height={40}
              px={1.5}
              mt={1}
              bgcolor="#ffffff"
              borderRadius="50px"
              sx={{ boxShadow: theme.shadows[2] }}
            >
              <Typography variant="body1" fontWeight={500}>
                {`${formatDate(new Date(startDate), FRONTEND_DATE_FORMAT_DATE_FNS)} - ${formatDate(
                  new Date(endDate),
                  FRONTEND_DATE_FORMAT_DATE_FNS
                )}`}
              </Typography>
            </Box>
          </MapControl>
        )}

        {onMarkerTypeChange && (
          <MapControl position={ControlPosition.TOP_RIGHT}>
            <MarkerTypeControl value={mapMarkerType} onChange={onMarkerTypeChange} sx={{ mt: 1, mr: 1 }} />
          </MapControl>
        )}

        <AdaptiveServiceMarkerLayer
          markers={deferredMarkers}
          mapMarkerType={mapMarkerType}
          activeMarker={activeMarker}
          selectedDaysMap={selectedDaysMap}
          selectedRoutesMap={selectedRoutesMap}
          onMouseEnter={handleMarkerMouseEnter}
          onMouseLeave={handleMarkerMouseLeave}
          radiusCircle={radiusCircle}
          centerMarker={centerMarker}
        />

        {showCenterElements && !webglMarkersActive && (
          <>
            <AdaptiveRadiusCircle
              center={centerLatLng}
              radius={Math.max(0, Math.round(radiusCommitted || 0))}
              strokeColor={theme.palette.primary.dark}
              strokeOpacity={1}
              strokeWeight={3}
              fillColor={theme.palette.primary.main}
              fillOpacity={0.3}
            />
            <AdvancedMarker zIndex={2} position={centerLatLng}>
              <Box
                backgroundColor={theme.palette.primary.main}
                width={40}
                height={40}
                borderRadius="50%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ cursor: 'pointer' }}
              >
                <SvgIcon sx={{ color: common.white }} width={24} height={24}>
                  <CSMarker />
                </SvgIcon>
              </Box>
            </AdvancedMarker>
          </>
        )}

        {infoWindowShown && activeMarker && (
          <StyledInfoWindow
            disableAutoPan
            // @vis.gl/react-google-maps treats `anchor={null}` as "anchor not
            // ready yet" and bails out — so for the WebGL path (no anchor) we
            // must pass `undefined` and rely on `position` instead.
            anchor={activeMarker.anchor || undefined}
            position={!activeMarker.anchor ? activeMarker.latLng : undefined}
            headerDisabled
          >
            <Box onMouseEnter={handlePopoverMouseEnter} onMouseLeave={handlePopoverMouseLeave}>
              {isLoadingPopover ? (
                <SkeletonSchedulingMapServicePopover />
              ) : (
                <SchedulingMapServicePopover service={enrichedService} weekWorkOrders={weekWorkOrders} />
              )}
            </Box>
          </StyledInfoWindow>
        )}
      </Map>
    </Box>
  )
}

ServiceProximityMap.propTypes = {
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  mapMarkerType: PropTypes.string,
  onMarkerTypeChange: PropTypes.func,
  filtersExpanded: PropTypes.bool,
}

export default ServiceProximityMap
